import { useRef, useState } from "react";
import { niceMax } from "./format";
import { ChartTooltip } from "./ChartTooltip";
import { useChartWidth } from "./useChartWidth";

const VIEW_H = 220;
const MARGIN = { top: 16, right: 16, bottom: 44, left: 64 };
const INNER_H = VIEW_H - MARGIN.top - MARGIN.bottom;
const MAX_BAR_THICKNESS = 40;
const SEGMENT_GAP = 2;

// A stacked bar chart in plain SVG — segments separated by a 2px surface-color
// gap (never a stroke, per the dataviz skill), 4px rounded cap only on the
// topmost segment of each stack, hover tooltip listing every segment + the
// stack total, legend for 2+ series, and a table-view fallback.
export const StackedBarChart = ({ data, seriesKeys, title, valueFormatter = (v) => v }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const containerRef = useRef(null);
  const viewW = useChartWidth(containerRef);
  const innerW = viewW - MARGIN.left - MARGIN.right;

  if (!data || data.length === 0) {
    return <p className="py-8 text-sm text-center text-gray-500">No data for this range.</p>;
  }

  const totals = data.map((row) => seriesKeys.reduce((s, k) => s + (row[k.key] || 0), 0));
  const maxValue = niceMax(Math.max(1, ...totals));
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxValue * f));

  const groupWidth = innerW / data.length;
  const barThickness = Math.min(MAX_BAR_THICKNESS, groupWidth * 0.6);

  const yFor = (value) => INNER_H - (Math.max(0, value) / maxValue) * INNER_H;
  const rotateLabels = data.length > 8;
  // With more than ~8 points, labels are wider than the space between them even
  // rotated — thin them out instead of letting them overlap/clip, always keeping
  // the most recent point's label.
  const labelStep = data.length > 8 ? Math.ceil(data.length / 8) : 1;
  const showLabel = (i) => i % labelStep === 0 || i === data.length - 1;

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex flex-wrap gap-4 items-center">
          {seriesKeys.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-sm font-bold whitespace-nowrap text-theme-color-1 hover:underline print:hidden"
        >
          {showTable ? "View as chart" : "View as table"}
        </button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-base divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-sm font-bold text-left text-gray-700 uppercase">Month</th>
                {seriesKeys.map((s) => (
                  <th key={s.key} className="px-3 py-2 text-sm font-bold text-right text-gray-700 uppercase">
                    {s.label}
                  </th>
                ))}
                <th className="px-3 py-2 text-sm font-bold text-right text-gray-700 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((row, i) => (
                <tr key={row.month || row.label}>
                  <td className="px-3 py-2 font-semibold text-gray-900">{row.label}</td>
                  {seriesKeys.map((s) => (
                    <td key={s.key} className="px-3 py-2 font-semibold text-right text-gray-700">
                      {valueFormatter(row[s.key] || 0)}
                    </td>
                  ))}
                  <td className="px-3 py-2 font-bold text-right text-gray-900">{valueFormatter(totals[i])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative" ref={containerRef}>
          <svg viewBox={`0 0 ${viewW} ${VIEW_H}`} width={viewW} height={VIEW_H} className="w-full h-[220px] overflow-visible" role="img" aria-label={title}>
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {yTicks.map((tick) => (
                <g key={tick}>
                  <line x1={0} x2={innerW} y1={yFor(tick)} y2={yFor(tick)} stroke="#e1e0d9" strokeWidth={1} />
                  <text x={-8} y={yFor(tick)} textAnchor="end" dominantBaseline="middle" fontSize={13} fontWeight={700} fill="#52514e">
                    {valueFormatter(tick)}
                  </text>
                </g>
              ))}
              <line x1={0} x2={innerW} y1={INNER_H} y2={INNER_H} stroke="#c3c2b7" strokeWidth={1} />

              {data.map((row, i) => {
                const x = i * groupWidth + (groupWidth - barThickness) / 2;
                const isHovered = hoverIndex === i;

                // stack segments bottom-up, leaving a 2px surface gap between them
                let cumulative = 0;
                const segments = seriesKeys
                  .map((s) => {
                    const raw = row[s.key] || 0;
                    const segTop = cumulative + raw;
                    cumulative = segTop;
                    return { key: s.key, color: s.color, from: segTop - raw, to: segTop, raw };
                  })
                  .filter((seg) => seg.raw > 0);

                return (
                  <g key={row.month || row.label}>
                    <rect
                      x={i * groupWidth}
                      y={0}
                      width={groupWidth}
                      height={INNER_H}
                      fill="transparent"
                      onMouseEnter={() => setHoverIndex(i)}
                      onMouseLeave={() => setHoverIndex(null)}
                      onFocus={() => setHoverIndex(i)}
                      onBlur={() => setHoverIndex(null)}
                      tabIndex={0}
                    />
                    {segments.map((seg, si) => {
                      const isTop = si === segments.length - 1;
                      const yTop = yFor(seg.to);
                      const yBottom = yFor(seg.from);
                      // Pull the top edge down (away from the segment above) unless this
                      // IS the top segment; pull the bottom edge up (away from the segment
                      // below) unless this is the bottom segment sitting on the baseline.
                      const gapAdjustedTop = yTop + (isTop ? 0 : SEGMENT_GAP / 2);
                      const gapAdjustedBottom = yBottom - (si === 0 ? 0 : SEGMENT_GAP / 2);
                      const h = Math.max(0, gapAdjustedBottom - gapAdjustedTop);
                      const r = isTop ? Math.min(4, barThickness / 2, h) : 0;
                      return (
                        <path
                          key={seg.key}
                          d={
                            !isTop || h <= r
                              ? `M${x},${gapAdjustedBottom} h${barThickness} v${-h} h${-barThickness} Z`
                              : `M${x},${gapAdjustedBottom} ` +
                                `H${x + barThickness} ` +
                                `V${gapAdjustedTop + r} ` +
                                `Q${x + barThickness},${gapAdjustedTop} ${x + barThickness - r},${gapAdjustedTop} ` +
                                `H${x + r} ` +
                                `Q${x},${gapAdjustedTop} ${x},${gapAdjustedTop + r} ` +
                                `Z`
                          }
                          fill={seg.color}
                          opacity={isHovered || hoverIndex === null ? 1 : 0.45}
                          pointerEvents="none"
                        />
                      );
                    })}
                    {showLabel(i) && (
                      <text
                        x={i * groupWidth + groupWidth / 2}
                        y={INNER_H + 18}
                        textAnchor={rotateLabels ? "end" : "middle"}
                        fontSize={13}
                        fontWeight={700}
                        fill="#3a3934"
                        transform={rotateLabels ? `rotate(-35 ${i * groupWidth + groupWidth / 2} ${INNER_H + 18})` : undefined}
                      >
                        {row.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

        </div>
      )}

      {hoverIndex !== null && !showTable && (
        <ChartTooltip anchorRef={containerRef} xRatio={(hoverIndex + 0.5) / data.length}>
          <p className="mb-1 font-bold text-gray-900">{data[hoverIndex].label}</p>
          {seriesKeys.map((s) => (
            <p key={s.key} className="flex items-center gap-1.5 text-gray-700">
              <span className="inline-block w-2 h-0.5 rounded" style={{ backgroundColor: s.color }} />
              <span className="font-bold">{valueFormatter(data[hoverIndex][s.key] || 0)}</span>
              <span className="font-semibold text-gray-500">{s.label}</span>
            </p>
          ))}
          <p className="pt-1 mt-1 font-bold text-gray-900 border-t">Total: {valueFormatter(totals[hoverIndex])}</p>
        </ChartTooltip>
      )}
    </div>
  );
};

export default StackedBarChart;
